import { Config, isLoopbackUrl } from "@amtana/module";
import { z } from "zod";

// How the vault wraps each organization's data key (ADR-023 §4). Production wraps with AWS KMS,
// through the host's own IAM role (no AWS key is configured here, as for SES). Development and
// tests wrap with a local key instead; production refuses the local wrapper, a missing KMS key and
// the development key, so a value sealed on a laptop can never be the one protecting a customer's.
// Unset, the local key is the development one; production never reads it, so it needs none.

/** 32 bytes, base64; a placeholder anyone who read the repository knows. */
export const DEV_VAULT_LOCAL_KEY = "ZGV2LXZhdWx0LWxvY2FsLWtleS1ub3QtYS1zZWNyZXQ=";

export const VAULT_KMS_KINDS = ["local", "aws-kms"] as const;

/** harnesssim (sims/harnesssim), standing in for Claude's and OpenAI's sign-in in development. */
export const DEV_HARNESS_SIGN_IN_BASE = "http://127.0.0.1:4328";

const simulator = (who: string) => (value: string) =>
  isLoopbackUrl(value) ? `points at a loopback host, the sign-in simulator playing ${who}` : undefined;

export const vaultConfig = Config.define((c) => ({
  kms: c.env("VAULT_KMS", z.enum(VAULT_KMS_KINDS).default("local"), {
    refuseInProduction: (value) =>
      value === "local" ? "wraps data keys with the local development key, not AWS KMS" : undefined,
  }),
  /** The KMS key's ARN or alias (`alias/zaistev-vault`); required with `VAULT_KMS=aws-kms`. */
  kmsKeyId: c.env("VAULT_KMS_KEY_ID", z.string().min(1).optional(), {
    refuseInProduction: (value) =>
      value === undefined ? "names no KMS key; production wraps every data key with AWS KMS" : undefined,
  }),
  kmsRegion: c.env(
    "VAULT_KMS_REGION",
    z
      .string()
      .regex(/^[a-z]{2}-[a-z]+-\d$/)
      .default("eu-west-1"),
  ),
  /** The local wrapper's key: base64 of exactly 32 bytes. */
  localKey: c.env(
    "VAULT_LOCAL_KEY",
    z
      .string()
      .regex(/^[A-Za-z0-9+/]{43}=$/, "needs base64 of exactly 32 bytes")
      .optional(),
    {
      refuseInProduction: (value) =>
        value === DEV_VAULT_LOCAL_KEY ? "still the development key, which anyone could read" : undefined,
    },
  ),
  /**
   * The member's own Claude or Codex login (ADR-023 §7, D628): where each provider's sign-in
   * lives. Claude's page is on one host (`https://claude.com`, its `/cai/oauth/authorize`) and its
   * token exchange on another (`https://platform.claude.com`, `/v1/oauth/token`, and the
   * `/oauth/code/callback` page that shows the code); OpenAI's device sign-in is all on
   * `https://auth.openai.com`. harnesssim answers all three in development and tests.
   */
  claudeAuthorizeBase: c.env("HARNESS_CLAUDE_AUTHORIZE_BASE", z.url().default(DEV_HARNESS_SIGN_IN_BASE), {
    refuseInProduction: simulator("Claude"),
  }),
  claudeApiBase: c.env("HARNESS_CLAUDE_API_BASE", z.url().default(DEV_HARNESS_SIGN_IN_BASE), {
    refuseInProduction: simulator("Claude"),
  }),
  openaiAuthBase: c.env("HARNESS_OPENAI_AUTH_BASE", z.url().default(DEV_HARNESS_SIGN_IN_BASE), {
    refuseInProduction: simulator("OpenAI"),
  }),
}));
